import * as React from 'react';
import cls from 'classnames';
import { setTransform, getPxStyle, UI } from './util';
import './style.scss';

export interface TabItemProps {
  key: string;
  value: string | JSX.Element;
}

interface TabPropType {
  /** 初始展示的页面 */
  initialPage?: number;
  /** 当前展示的页面 */
  page: number;
  /** 切换tab的滑动距离 */
  distanceToChangeTab: number;
  /** 切换tab的时间 */
  durationToChangeTab: number;
  /** tabs */
  tabs: Array<TabItemProps>;
  /** 切换tab的回调函数 */
  onChange: (tab: any, index: number) => void;
  /** 子元素 */
  children: Array<React.ReactElement<any>>;
  /** 是否可以滑动切换 */
  swipeable: ((event: any) => boolean) | boolean;
  /** 容器类名 */
  className?: string;
  /** 支持自定义tab content的最小高度 */
  minHeight?: number;
  /** 是否禁用点击tab切换 */
  tabable?: boolean;
  /** 是否开启计算page后首次切换动画 */
  activeable?: boolean;
}

interface TabState {
  page: number;
  tabIndexWidth: string | number;
  minHeight: string;
  setActiveTab: boolean;
}

interface XY {
  x: number;
  y: number;
}

export class SwiperTabs extends React.Component<TabPropType, TabState> {
  static defaultProps = {
    page: 0,
    distanceToChangeTab: 0.3,
    durationToChangeTab: 600,
    className: '',
    tabable: true,
    activeable: true,
  };
  readonly state = {
    page: this.props.initialPage || this.props.page || 0,
    tabIndexWidth: 'auto',
    isMoving: false,
    minHeight: '100vh',
    setActiveTab: false,
  };
  private _tabbar: any = null;
  private _tabtitle: any[] = [];
  private _tabindex: any = null;
  private _tabindexBar: any = null;
  private _tablist: any = null;
  private _tablistItem: any[] = [];
  private _isSwipeScroll = false;
  // 用来记录初始滑动的位置
  private _swipeOffset: XY = { x: 0, y: 0 };
  // 记录开始滑动的事件，用于实现快速滑动
  private _swipeStartTimeStamp = 0;
  // 是否可以滑动
  private _swipeable = true;

  constructor(props: TabPropType) {
    super(props);
  }

  componentDidMount() {
    const { page } = this.state;

    // 由于页面rem设置、css渲染有一定延迟，需要delay计算一下
    setTimeout(() => {
      if (this._tabtitle && this._tabtitle[page]) {
        this.setState({
          tabIndexWidth: UI.offset(this._tabtitle[page], 'width'),
          minHeight: `${UI.innerHeight() - UI.offset(this._tabbar, 'height')}px`,
        });
      }
    }, 50);

    this._listenToCustomEvent();
    this.enableActiveTab();
  }

  componentWillUnmount() {
    this._tablist.removeEventListener('touchstart', this.onTouchStart, false);
    this._tablist.removeEventListener('touchmove', this.onTouchMove, false);
    this._tablist.removeEventListener('touchend', this.onTouchEnd, false);
    this._tablist.removeEventListener('touchcancel', this.onTouchEnd, false);
  }
  enableActiveTab() {
    this.setState({
      setActiveTab: true,
    });
  }

  _listenToCustomEvent() {
    // 给dom绑定原生的事件监听，为了阻止客户端touch的事件
    this._tablist.addEventListener('touchstart', this.onTouchStart, {
      passive: false,
    });
    this._tablist.addEventListener('touchmove', this.onTouchMove, {
      passive: false,
    });
    this._tablist.addEventListener('touchend', this.onTouchEnd, {
      passive: false,
    });
    this._tablist.addEventListener('touchcancel', this.onTouchEnd, {
      passive: false,
    });
  }

  setTransformX = (page: number, rateX = 0) => {
    // setState太影响性能，这里直接修改dom；这里很trick，逻辑耦合，在父组件里做了reset逻辑
    this._tablistItem.forEach((item: any) => {
      setTransform(item.style, getPxStyle((-page - rateX) * 100, '%'));
    });
    // 移动中，只是修改css，因为两层传递，所以这里只修改tab的css
    setTransform(this._tabindex.style, getPxStyle((page + rateX) * 100, '%'));

    const next = page + (rateX > 0 ? 1 : -1);
    if (next >= 0 && next < this.props.tabs.length) {
      const curWidth = UI.offset(this._tabtitle[page], 'width');
      const nextWidth = UI.offset(this._tabtitle[next], 'width');
      const width = rateX > 0 ? curWidth + (nextWidth - curWidth) * rateX : curWidth + (curWidth - nextWidth) * rateX;
      this._tabindexBar.style.width = `${width}px`;
    } else {
      const curWidth = UI.offset(this._tabtitle[page], 'width');
      this._tabindexBar.style.width = `${curWidth}px`;
    }
  };

  onChange = (index: number, type: 'swipe' | 'click') => {
    const { tabs, tabable } = this.props;
    if (tabable) {
      this.setState({
        page: index,
        tabIndexWidth: UI.offset(this._tabtitle[index], 'width'),
      });
    }
    this.props.onChange(tabs[index].key, index);
  };

  canSwipe = (e: any) => {
    if (typeof this.props.swipeable === 'function') {
      return this.props.swipeable(e);
    }
    return this.props.swipeable;
  };

  // 关于滑动相关的逻辑，耦合严重，后续需要优化
  onTouchStart = (e: any) => {
    // 如果两次滑动间隔过小，认为是误操作，不滑动
    if (Date.now() - this._swipeStartTimeStamp < 5e2) {
      this._swipeable = false;
      return;
    }
    this._swipeable = this.canSwipe(e);
    if (!this._swipeable) {
      return;
    }
    // 关闭动画效果，为了加速触摸响应
    this._tablist.classList.remove('section-list-animated');
    this._tabindex.classList.remove('tab-index-animated');
    // e.preventDefault();

    // 缓存滑动开始的位置
    this._swipeOffset = {
      x: e.changedTouches[0].pageX,
      y: e.changedTouches[0].pageY,
    };
    // 滑动开始时间
    this._swipeStartTimeStamp = Date.now();
  };
  onTouchMove = (e: any) => {
    if (!this._swipeable) {
      return;
    }
    const delta = {
      x: e.changedTouches[0].pageX - this._swipeOffset.x,
      y: e.changedTouches[0].pageY - this._swipeOffset.y,
    };

    // 如果上下滑动大于左右滑动那么认为在滚动中，则不进行左右滑动
    if ((!this._isSwipeScroll && Math.abs(delta.x) < Math.abs(delta.y)) || this._isSwipeScroll) {
      // 滑动交互中的滚动锁
      this._isSwipeScroll = true;
      return;
    }

    // 在某些版本的浏览器里，可以对touch的默认事件进行取消操作
    // 判断默认行为是否可以被禁用
    if (e.cancelable) {
      // 判断默认行为是否已经被禁用
      if (!e.defaultPrevented) {
        e.preventDefault();
      }
    }

    const { page } = this.state;

    // 如果当前list为边界，则第一个list不往左滑动，最后一个list不往右滑动
    if ((page === 0 && delta.x > 0) || (page === this.props.tabs.length - 1 && delta.x < 0)) {
      return;
    }

    const width = UI.innerWidth();
    const rateX = -delta.x / width;

    // setState太影响性能，这里直接修改dom；这里很trick，逻辑耦合，在父组件里做了reset逻辑
    this.setTransformX(page, rateX);
  };
  onTouchEnd = async (e: any) => {
    if (!this._swipeable) {
      return;
    }
    // 重新开启动画效果
    this._tablist.classList.add('section-list-animated');
    this._tabindex.classList.add('tab-index-animated');

    const delta = {
      x: e.changedTouches[0].pageX - this._swipeOffset.x,
      y: e.changedTouches[0].pageY - this._swipeOffset.y,
    };
    const { tabs, distanceToChangeTab, durationToChangeTab } = this.props;
    const { page } = this.state;
    const width = UI.innerWidth();
    const rateX = -delta.x / width;
    // 移动结束后，需要做左右滑动判定
    const tabNum = tabs.length;
    // rate 正数：右滑，负数：左滑
    let index = rateX < 0 ? page - 1 : page + 1;
    const duration = Date.now() - this._swipeStartTimeStamp;
    /**
     * 由于客户端交互滑动有两种：
     * - 轻触滑动，碰一下就左右滑动tab
     * - 拖拽，超过50%后才滑动tab
     * 这里采用折中方案，轻触和拖拽，超过10%就滑动，防止误触和过快滑动
     */
    // console.log('duration:', duration);
    // console.log('rateX:', rateX);
    // console.log('dy,height:', delta.y, UI.innerHeight());
    // console.log('rateY:', Math.abs(delta.y / UI.innerHeight()));
    // console.log('velocity:', (Math.abs(rateX) / duration) * 1000);
    if (
      // 如果滑动中出现了上下滑动，并且上下滑动大于0
      (this._isSwipeScroll && Math.abs(delta.y / UI.innerHeight()) > 0) ||
      // 如果到了边界，那么不变
      index < 0 ||
      index >= tabNum ||
      // 如果移动距离小于可拖动范围
      (duration >= durationToChangeTab && Math.abs(rateX) < distanceToChangeTab) ||
      // 如果移动时间过短认为是拖拽，如果velocity < 0.3/s
      (duration < durationToChangeTab && (Math.abs(rateX) / duration) * 1000 < 0.3)
    ) {
      index = page;
      // reset transform
      this.setTransformX(index);
    }
    // 对于滑动而言，只有切换tab才会触发逻辑
    if (page !== index) {
      this.setTransformX(index);
      setTimeout(() => {
        this.onChange(index, 'swipe');
      }, 300);
    }

    // 如果结束时是滚动中，那么去掉滚动
    if (this._isSwipeScroll) {
      this._isSwipeScroll = false;
    }
  };

  render() {
    const { tabs, className, minHeight: customMinHeight, activeable } = this.props;
    const { tabIndexWidth, page: initPage, minHeight: calculateMinHeight, setActiveTab } = this.state;
    const minHeight = customMinHeight !== undefined ? `${customMinHeight}px` : calculateMinHeight;
    const page = !activeable || setActiveTab ? initPage : 0;

    return (
      <React.Fragment>
        <div
          className={cls(className, 'ui-mobile-swipe-tab-container', 'tab-container', `tab-container-${tabs.length}`)}
          ref={(elem: any) => (this._tabbar = elem)}
        >
          <ul className="tab-list">
            {tabs.map((tab, index: number) => (
              <li
                className={cls('tab-item', {
                  active: page === index,
                })}
                key={index}
                onClick={() => this.onChange(index, 'click')}
              >
                <span ref={(elem: any) => (this._tabtitle[index] = elem)}>{tab.value}</span>
              </li>
            ))}
          </ul>
          <div
            className={cls('tab-index tab-index-animated', `tab-${page}`)}
            style={{
              width: `${(100 / tabs.length).toFixed(2)}%`,
              transform: `translate3d(${page * 100}%, 0px, 0px)`,
              WebkitTransform: `translate3d(${page * 100}%, 0px, 0px)`,
              OTransform: `translate3d(${page * 100}%, 0px, 0px)`,
            }}
            ref={(elem: any) => (this._tabindex = elem)}
          >
            <span
              className="tab-index-bar"
              style={{ width: `${tabIndexWidth}px` }}
              ref={(elem: any) => (this._tabindexBar = elem)}
            />
          </div>
        </div>
        <div id="slider" className="section-container">
          <div className="section-list section-list-animated" ref={(elem: any) => (this._tablist = elem)}>
            {this.props.children.map((child: any, index: number) => (
              <section
                key={index}
                className={cls('section-item', {
                  'section-item-inactive': page !== index,
                  'section-item-active': page === index,
                })}
                style={{
                  minHeight,
                  transform: `translate3d(${-page * 100}%, 0px, 0px)`,
                  WebkitTransform: `translate3d(${-page * 100}%, 0px, 0px)`,
                  OTransform: `translate3d(${-page * 100}%, 0px, 0px)`,
                }}
                ref={(elem: any) => (this._tablistItem[index] = elem)}
              >
                {child}
              </section>
            ))}
          </div>
        </div>
      </React.Fragment>
    );
  }
}
